import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Loader2, ArrowRight, Building2, Mail, ArrowLeft } from "lucide-react";
import { CompactTrustRow } from "@/components/PaymentTrust";
import { Brand } from "@/components/Brand";
import { OtpInput } from "@/components/OtpInput";
import { startPlanCheckout } from "@/lib/checkout";
import { trackMeta } from "@/lib/meta";
import { logEvent } from "@/lib/analytics";
import { getAttribution } from "@/lib/attribution";
import { isPersonalEmail } from "@/lib/authHelpers";

const SIGNUP_PLAN_KEYS = ["PAYG", "Essential", "Starter", "Growth", "Professional"];
const JK = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

export default function Signup() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const planParam = params.get("plan");
  const initialPlan = SIGNUP_PLAN_KEYS.includes(planParam) ? planParam : "trial";
  const billingPeriod = params.get("billing") === "annual" ? "annual" : "monthly";

  const [opts, setOpts] = useState({ industries: [], plans: [], trial_days: 7, purchasable_plans: [] });
  const [form, setForm] = useState({ name: "", email: "", company_name: "", phone: "", whatsapp: false, industry: "generic", plan: initialPlan });
  const [step, setStep] = useState("form"); // "form" | "otp"
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [paygPlan, setPaygPlan] = useState(null);

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim().toLowerCase());
  const personalEmail = emailValid && isPersonalEmail(form.email);
  const canContinue = !!form.name.trim() && emailValid && !!form.company_name.trim();

  useEffect(() => {
    api.get("/public/signup-options").then((r) => setOpts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/public/pricing")
      .then((r) => setPaygPlan((r.data?.plans || []).find((p) => p.key === "PAYG" || p.is_payg) || null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const requestOtp = async ({ resend = false } = {}) => {
    const email = form.email.trim().toLowerCase();
    setBusy(true);
    try {
      await api.post("/auth/otp/request", {
        email, purpose: "signup", name: form.name.trim(), company_name: form.company_name.trim(),
        phone: form.phone.trim(), whatsapp_opt_in: form.whatsapp, industry: form.industry,
        plan: form.plan, billing_period: billingPeriod, attribution: getAttribution(),
      });
      setStep("otp");
      setCooldown(60);
      toast.success(resend ? "New code sent" : `We've sent a 6-digit code to ${email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send verification code");
    } finally { setBusy(false); }
  };

  const submitForm = (e) => {
    e.preventDefault();
    if (!canContinue) return toast.error("Please fill name, a valid work email and company name");
    if (form.phone.trim() && form.phone.replace(/\D/g, "").length < 10) {
      return toast.error("Please enter a valid phone number, or leave it blank");
    }
    requestOtp();
  };

  const finishSignup = async (res) => {
    const email = form.email.trim().toLowerCase();
    localStorage.removeItem("active_tenant_id");
    localStorage.setItem("auth_token", res.data.token);
    setUser(res.data.user);
    trackMeta("CompleteRegistration", { customData: { plan: form.plan, industry: form.industry, registration_method: "email_otp" }, email, phone: form.phone });
    trackMeta("StartTrial", { customData: { plan: form.plan, trial_length_days: opts.trial_days || 7 }, email, phone: form.phone });
    logEvent("signup_completed", { plan: form.plan, billing_period: billingPeriod });
    // Only send to Razorpay for a plan the server actually allows to be purchased right now
    // (flag-aware). PAYG (₹0) and non-purchasable plans degrade to the workspace, never a ₹0 order.
    const purchasable = opts.purchasable_plans || [];
    const shouldCheckout = form.plan !== "trial" && form.plan !== "PAYG" && purchasable.includes(form.plan);
    if (shouldCheckout) {
      toast.success("Workspace created — complete payment to activate your plan.");
      await startPlanCheckout({
        plan: form.plan,
        billingPeriod,
        onSuccess: ({ paymentId }) => { navigate(`/payment-success?plan=${encodeURIComponent(form.plan)}${paymentId ? `&payment_id=${encodeURIComponent(paymentId)}` : ""}`); },
        onDismiss: () => { toast.info("You're on a free trial — upgrade anytime from Billing."); navigate("/import"); },
        onFailure: (msg) => { toast.error(`${msg}. You're on a free trial — retry upgrade from Billing.`); navigate("/import"); },
      });
      return;
    }
    toast.success("Workspace created! Let's get you set up.");
    navigate("/welcome");
  };

  const verifyOtp = async (submitCode) => {
    const otp = (submitCode || code).trim();
    if (otp.length !== 6) return toast.error("Enter the 6-digit code");
    const email = form.email.trim().toLowerCase();
    setBusy(true);
    try {
      const res = await api.post("/auth/otp/verify", { email, purpose: "signup", code: otp, trust_device: trustDevice });
      await finishSignup(res);
    } catch (err) {
      setCode("");
      toast.error(err.response?.data?.detail || "Invalid or expired code");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#0B3D91] text-white p-12">
        <Link to="/" className="flex items-center gap-2" data-testid="signup-brand">
          <Brand dark iconSize={36} textClass="text-xl" style={JK} />
        </Link>
        <div>
          <h2 className="text-3xl xl:text-4xl font-extrabold leading-tight" style={JK}>Start recovering more, in minutes.</h2>
          <p className="mt-4 text-blue-100 max-w-md">Your free {opts.trial_days}-day trial includes everything you need to track receivables, follow-ups, settlements and recoveries — one workflow for any industry.</p>
          <ul className="mt-8 space-y-3">
            {["No password to remember", "Your own isolated workspace", "Setup in minutes", "Cancel anytime"].map((b) => (
              <li key={b} className="flex items-center gap-3 text-blue-50"><Check className="h-5 w-5 text-[#7ef0a9]" />{b}</li>
            ))}
          </ul>
        </div>
        <p className="text-sm text-blue-200">© {new Date().getFullYear()} RecoverOS · Trustera Services</p>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center bg-slate-50 px-6 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Brand iconSize={30} textClass="text-lg" style={JK} />
          </div>

          {step === "form" ? (
            <>
              <h1 className="text-2xl font-extrabold text-slate-900" style={JK}>Create your RecoverOS account</h1>
              <p className="mt-1 text-sm text-slate-500">Start your {opts.trial_days}-day free trial — no password needed.</p>

              <div className="mt-4 flex items-start gap-2 rounded-sm border border-[#0B3D91]/20 bg-[#0B3D91]/5 px-3 py-2" data-testid="signup-business-banner">
                <Building2 className="h-4 w-4 text-[#0B3D91] shrink-0 mt-0.5" />
                <p className="text-xs text-slate-600">
                  <b className="text-slate-800">RecoverOS is built for businesses.</b> Use your work email — we'll email you a verification code to sign in, no password to remember.
                </p>
              </div>

              <form onSubmit={submitForm} className="mt-6 space-y-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Full Name *</Label>
                  <Input value={form.name} onChange={set("name")} placeholder="Your name" className="rounded-none mt-1" data-testid="signup-name" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Work Email *</Label>
                  <Input type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className="rounded-none mt-1" data-testid="signup-email" />
                  {personalEmail && (
                    <p className="mt-1 text-xs text-amber-600" data-testid="signup-personal-email-hint">
                      Using a personal email is absolutely fine. For business use, your company email is recommended.
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Company / Organization *</Label>
                  <Input value={form.company_name} onChange={set("company_name")} placeholder="Acme Finance Pvt Ltd" className="rounded-none mt-1" data-testid="signup-company" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Phone / WhatsApp <span className="font-normal text-slate-400">(optional)</span></Label>
                  <Input value={form.phone} onChange={set("phone")} placeholder="+91 98xxxxxxxx" className="rounded-none mt-1" data-testid="signup-phone" />
                  <label className="mt-2 flex items-center gap-2 cursor-pointer select-none">
                    <input type="checkbox" checked={form.whatsapp} onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.checked }))} className="h-4 w-4 accent-[#16A34A]" data-testid="signup-whatsapp" />
                    <span className="text-xs text-slate-600">I prefer to be contacted on WhatsApp</span>
                  </label>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Industry <span className="font-normal text-slate-400">(optional)</span></Label>
                  <Select value={form.industry} onValueChange={(v) => setForm((f) => ({ ...f, industry: v }))}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="signup-industry"><SelectValue placeholder="Select your industry" /></SelectTrigger>
                    <SelectContent>
                      {opts.industries.map((i) => (<SelectItem key={i.key} value={i.key}>{i.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-slate-500" data-testid="signup-trial-note">Your Intro PAYG workspace includes <b>up to {paygPlan?.account_limit ?? 100} accounts · {paygPlan?.user_limit ?? 1} user · up to 250 email communications</b> (with up to {paygPlan?.free_rc_monthly ?? 50} Free RC/month). ₹0 for 3 months — pick a paid plan anytime when you're ready.</p>
                </div>

                <button type="submit" disabled={busy || !canContinue} className="w-full bg-[#16A34A] text-white rounded-none px-6 py-3 font-semibold hover:bg-[#138a3e] transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-testid="signup-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {busy ? "Sending code…" : "Continue"}
                  {!busy && <ArrowRight className="h-4 w-4" />}
                </button>
              </form>

              <CompactTrustRow className="mt-4" />
              <p className="mt-6 text-sm text-slate-500 text-center">
                Already have an account?{" "}
                <button onClick={() => navigate("/signin")} className="font-semibold text-[#0B3D91] hover:underline" data-testid="signup-to-login">Log in</button>
              </p>
            </>
          ) : (
            <div data-testid="signup-otp-step">
              <button onClick={() => { setStep("form"); setCode(""); }} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0B3D91] mb-4" data-testid="signup-otp-back">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-[#0B3D91]/10 mb-4"><Mail className="h-6 w-6 text-[#0B3D91]" /></div>
              <h1 className="text-2xl font-extrabold text-slate-900" style={JK}>Verify your email</h1>
              <p className="mt-1 text-sm text-slate-500">We've sent a 6-digit verification code to:</p>
              <p className="text-sm font-semibold text-slate-800" data-testid="signup-otp-email">{form.email.trim().toLowerCase()}</p>

              <form onSubmit={(e) => { e.preventDefault(); verifyOtp(); }} className="mt-6 space-y-5">
                <OtpInput value={code} onChange={setCode} onComplete={(v) => verifyOtp(v)} disabled={busy} testId="signup-otp" />
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} className="h-4 w-4 accent-[#0B3D91]" data-testid="signup-trust-device" />
                  <span className="text-xs text-slate-600">Trust this device for 30 days (skip the code next time)</span>
                </label>
                <button type="submit" disabled={busy || code.length !== 6} className="w-full bg-[#16A34A] text-white rounded-none px-6 py-3 font-semibold hover:bg-[#138a3e] transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-testid="signup-otp-verify">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {busy ? "Verifying…" : "Verify & Continue"}
                </button>
              </form>

              <div className="mt-5 text-center text-sm text-slate-500">
                Didn't get the code?{" "}
                {cooldown > 0 ? (
                  <span data-testid="signup-otp-cooldown">Resend in {cooldown}s</span>
                ) : (
                  <button onClick={() => requestOtp({ resend: true })} disabled={busy} className="font-semibold text-[#0B3D91] hover:underline" data-testid="signup-otp-resend">Resend code</button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
